/**
 * PhotoManagementDialog component - manages photos for a single user.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { UserPhotoStatus, ProfilePhotoDetail } from '../types';
import PhotoGallery from './PhotoGallery';
import PhotoUploader from './PhotoUploader';
import { SharePointBrowser } from '../../../components/shared';
import type { SharePointItem, SharePointSite } from '../../../components/shared/types/sharepoint';
import { profilePictureApi } from '../services/profilePictureApi';
import { useAuth } from '../../../contexts/AuthContext';

interface PhotoManagementDialogProps {
  user: UserPhotoStatus | null;
  open: boolean;
  onClose: () => void;
  onUserUpdate?: (updatedUser: Partial<UserPhotoStatus> & { user_id: string }) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`photo-tabpanel-${index}`}
      aria-labelledby={`photo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const PhotoManagementDialog: React.FC<PhotoManagementDialogProps> = ({
  user,
  open,
  onClose,
  onUserUpdate,
}) => {
  const { getAccessToken } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [graphPhotoUrl, setGraphPhotoUrl] = useState<string | null>(null);
  const [photos, setPhotos] = useState<ProfilePhotoDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingJobs, setUploadingJobs] = useState<Map<string, { jobId: string; tempId: string }>>(new Map());

  const loadPhotos = useCallback(async () => {
    if (!user) return;

    const token = await getAccessToken();
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await profilePictureApi.getUserPhotos(user.user_id, token);
      console.log('Fetched photos:', response.photos);

      // Load thumbnails for each photo
      const photosWithThumbnails = await Promise.all(
        response.photos.map(async (photo) => {
          try {
            console.log(`Loading thumbnail for photo ${photo.id}`);
            const thumbnailUrl = await profilePictureApi.getPhotoContent(photo.id, token);
            console.log(`Thumbnail loaded for ${photo.id}:`, thumbnailUrl);
            return { ...photo, thumbnailUrl: thumbnailUrl || undefined };
          } catch (err) {
            console.error(`Failed to load thumbnail for photo ${photo.id}:`, err);
            return photo;
          }
        })
      );

      console.log('Photos with thumbnails:', photosWithThumbnails);
      setPhotos(photosWithThumbnails);
    } catch (err: any) {
      console.error('Error loading photos:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken]);

  const loadGraphPhoto = useCallback(async () => {
    if (!user || !user.has_graph_photo) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      const url = await profilePictureApi.getUserGraphPhoto(user.user_id, token);
      if (url) {
        setGraphPhotoUrl(url);
      }
    } catch (err) {
      console.error('Error loading Graph photo:', err);
    }
  }, [user, getAccessToken]);

  useEffect(() => {
    if (open && user) {
      // Clear previous data immediately to avoid showing old user's data
      setPhotos([]);
      setGraphPhotoUrl(null);
      setError(null);
      setSuccess(null);
      setTabValue(0);

      // Then load new data
      loadPhotos();
      loadGraphPhoto();
    }

    // Note: Don't revoke blob URLs on cleanup - they're still needed for rendering
    // Browser will clean them up automatically when the page is unloaded
  }, [open, user, loadPhotos, loadGraphPhoto]);

  const handleUpload = async (file: File) => {
    if (!user) return;

    const token = await getAccessToken();
    if (!token) return;

    try {
      await profilePictureApi.uploadUserPhoto(user.user_id, file, token);
      setSuccess('Photo uploaded successfully!');
      await loadPhotos();

      // Update the user card with new photo count
      if (onUserUpdate) {
        onUserUpdate({
          user_id: user.user_id,
          sharepoint_photo_count: photos.length + 1,
        });
      }

      // Switch to gallery tab
      setTabValue(0);
    } catch (err: any) {
      throw err; // Let PhotoUploader handle the error display
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    if (!user) return;

    const token = await getAccessToken();
    if (!token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profilePictureApi.setPrimaryPhoto(user.user_id, { photo_id: photoId }, token);
      setSuccess('Primary photo set and synced to Microsoft Graph!');
      await loadPhotos();
      await loadGraphPhoto();

      // Update the user card with new photo status
      if (onUserUpdate) {
        onUserUpdate({
          user_id: user.user_id,
          has_graph_photo: true,
          sharepoint_photo_count: photos.length,
          sync_status: 'synced',
          graph_photo_last_updated: new Date().toISOString(),
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to set primary photo';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to remove this photo association?')) {
      return;
    }

    const token = await getAccessToken();
    if (!token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profilePictureApi.deleteUserPhoto(user.user_id, photoId, false, token);
      setSuccess('Photo association removed successfully!');
      await loadPhotos();

      // Update the user card with new photo count
      if (onUserUpdate) {
        onUserUpdate({
          user_id: user.user_id,
          sharepoint_photo_count: Math.max(0, photos.length - 1),
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSharePointPhotoSelect = async (item: SharePointItem, _site: SharePointSite, _driveId: string) => {
    if (!user) return;

    const token = await getAccessToken();
    if (!token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profilePictureApi.associateExistingPhoto(user.user_id, item.id, token);
      setSuccess(`Photo "${item.name}" associated successfully!`);
      await loadPhotos();
      // Don't refresh the entire user list, just reload this user's photos

      // Switch to gallery tab to show the newly associated photo
      setTabValue(0);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to associate photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCroppedPhoto = async (_photoId: string, croppedBlob: Blob) => {
    const startTime = performance.now();
    console.log('[PhotoDialog] 🚀 Starting async upload for cropped photo, size:', (croppedBlob.size / 1024).toFixed(0), 'KB');

    if (!user) return;

    const token = await getAccessToken();
    if (!token) return;

    setError(null);
    setSuccess(null);

    try {
      // Create a File from the blob
      const file = new File([croppedBlob], 'cropped_headshot.jpg', { type: 'image/jpeg' });

      // Upload async - returns immediately with job ID
      const uploadStartTime = performance.now();
      const jobResponse = await profilePictureApi.uploadUserPhotoAsync(user.user_id, file, token);
      console.log(`[PhotoDialog] ✅ Upload queued (+${(performance.now() - uploadStartTime).toFixed(0)}ms)`);

      // Generate temporary ID for optimistic UI update
      const tempId = `temp-${Date.now()}`;

      // Add placeholder photo to the list immediately with "uploading" status
      setPhotos(prev => [...prev, {
        id: tempId,
        filename: file.name,
        web_url: '',
        file_size: file.size,
        primary: false,
        last_modified: new Date().toISOString(),
        // Store job info in a special field that we'll check
      } as any]);

      // Track the upload job
      setUploadingJobs(prev => new Map(prev).set(tempId, { jobId: jobResponse.job_id, tempId }));

      setSuccess('Photo upload started! Progress will be shown in the gallery.');

      // Start polling for status
      pollUploadStatus(jobResponse.job_id, tempId, token);

      console.log(`[PhotoDialog] ✅ Total time: ${(performance.now() - startTime).toFixed(0)}ms`);
    } catch (err: any) {
      console.error('[PhotoDialog] ❌ Error saving cropped photo:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save cropped photo');
    }
  };

  const pollUploadStatus = async (jobId: string, tempId: string, token: string) => {
    const maxAttempts = 60; // Poll for up to 1 minute
    const pollInterval = 1000; // Poll every second
    let attempts = 0;

    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        console.log('[PhotoDialog] ⏰ Polling timeout for job', jobId);
        setError('Upload is taking longer than expected. Please refresh to see if it completed.');
        return;
      }

      attempts++;

      try {
        const status = await profilePictureApi.getUploadStatus(jobId, token);
        console.log(`[PhotoDialog] 📊 Job ${jobId} status:`, status.status, `(attempt ${attempts})`);

        if (status.status === 'completed' && status.photo_id) {
          // Upload successful! Replace temp photo with real one
          console.log('[PhotoDialog] ✅ Upload completed successfully!');

          // Fetch the completed photo details
          await loadPhotos();

          // Remove from uploading jobs
          setUploadingJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });

          // Update user card
          if (onUserUpdate && user) {
            onUserUpdate({
              user_id: user.user_id,
              sharepoint_photo_count: photos.length + 1,
            });
          }

          setSuccess('Photo uploaded successfully!');
        } else if (status.status === 'failed') {
          // Upload failed
          console.log('[PhotoDialog] ❌ Upload failed:', status.error_message);

          // Remove temp photo from list
          setPhotos(prev => prev.filter(p => p.id !== tempId));

          // Remove from uploading jobs
          setUploadingJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });

          setError(status.error_message || 'Upload failed after retries');
        } else if (status.status === 'pending' || status.status === 'processing') {
          // Still processing, continue polling
          console.log(`[PhotoDialog] ⏳ Upload ${status.status}, continuing to poll...`);
          setTimeout(checkStatus, pollInterval);
        } else {
          // Unknown status - log and continue polling
          console.warn(`[PhotoDialog] ⚠️ Unknown status: ${status.status}, continuing to poll...`);
          setTimeout(checkStatus, pollInterval);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          // Job not found - this is an error with durable status tracking
          console.error('[PhotoDialog] ❌ Job not found - upload may have been lost');

          // Remove temp photo from list
          setPhotos(prev => prev.filter(p => p.id !== tempId));

          // Remove from uploading jobs
          setUploadingJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });

          setError('Upload job not found. Please try uploading again.');
        } else {
          console.error('[PhotoDialog] ❌ Error polling status:', err);
          // Retry polling on network errors, but stop after too many attempts
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, pollInterval);
          } else {
            // Remove temp photo and give up
            setPhotos(prev => prev.filter(p => p.id !== tempId));
            setUploadingJobs(prev => {
              const newMap = new Map(prev);
              newMap.delete(tempId);
              return newMap;
            });
            setError('Upload status check failed. Please refresh to see if upload completed.');
          }
        }
      }
    };

    // Start polling
    checkStatus();
  };

  const handleClose = () => {
    setTabValue(0);
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar src={graphPhotoUrl || undefined} sx={{ width: 56, height: 56 }}>
            {!graphPhotoUrl && <Person />}
          </Avatar>
          <Box>
            <Typography variant="h6">{user.display_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={`Photos (${photos.length})`} />
          <Tab label="Upload New" />
          <Tab label="Browse SharePoint" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {loading && photos.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <PhotoGallery
              photos={photos}
              onSetPrimary={handleSetPrimary}
              onDelete={handleDelete}
              onSaveCropped={handleSaveCroppedPhoto}
              disabled={loading}
              uploadingJobs={uploadingJobs}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <PhotoUploader onUpload={handleUpload} disabled={loading} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ height: '500px', mt: 2 }}>
            <SharePointBrowser
              mode="file"
              title="Marketing Headshots"
              description="Select a photo to associate with this user"
              onSelect={handleSharePointPhotoSelect}
              initialSite="marketing"
              initialDriveName="Marketing Collateral"
              initialPath="For the Marketing team only/Headshots"
              fileTypeFilter={['.jpg', '.jpeg', '.png', '.gif']}
              defaultItemType="files"
              hideFilters={true}
              hideBreadcrumb={true}
              showTitle={false}
            />
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoManagementDialog;
